'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

const navItems = [
  { href: '/', label: 'Video Library', icon: 'PlayCircleIcon', badge: null },
];

const sections = [
  { id: 'my-profile', label: 'My Profile', count: 5, icon: 'UserIcon' },
  { id: 'member-sign', label: 'Member Sign', count: 2, icon: 'IdentificationIcon' },
  { id: 'member-list', label: 'Member List', count: 6, icon: 'UsersIcon' },
  { id: 'group-meeting-admin', label: 'Group Meeting (Admin)', count: 4, icon: 'PresentationChartBarIcon' },
  { id: 'events', label: 'Events', count: 1, icon: 'CalendarIcon' },
  { id: 'group-meeting-member', label: 'Group Meeting (Member)', count: 1, icon: 'UserGroupIcon' },
  { id: 'request-category', label: 'Request Category', count: 1, icon: 'TagIcon' },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-40 bg-card border-r border-border flex flex-col
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-64'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className={`flex items-center gap-2 px-4 py-4 border-b border-border ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div>
              <span className="font-bold text-sm text-foreground leading-tight block">BBNG</span>
              <span className="text-xs text-muted-foreground leading-tight block">Video Library</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={16} />
          </button>
        </div>

        {/* Main nav */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {!collapsed && (
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-widest px-2 pt-2 pb-1">Navigation</p>
          )}
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                className={`sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`}
                title={collapsed ? item.label : undefined}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                {!collapsed && <span>{item.label}</span>}
                {!collapsed && item.badge && (
                  <span className="ml-auto badge-section">{item.badge}</span>
                )}
              </Link>
            );
          })}

          {!collapsed && (
            <>
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-widest px-2 pt-4 pb-1">Sections</p>
              {sections.map((section) => {
                const isActive = pathname === '/' && false; // highlight on scroll
                return (
                  <Link
                    key={`section-${section.id}`}
                    href={`/#${section.id}`}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon name={section.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-muted-foreground flex-shrink-0" />
                    <span className="truncate text-xs">{section.label}</span>
                    <span className="ml-auto badge-section">{section.count}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

      </aside>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-card/95 backdrop-blur border-b border-border flex items-center justify-between px-4 lg:px-6 h-14">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Icon name="Bars3Icon" size={20} />
            </button>
            <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="HomeIcon" size={14} />
              <span>/</span>
              <span className="text-foreground font-500">
                {pathname === '/' ? 'Video Library' : pathname === '/video-player' ? 'Video Player' : ''}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}